import { buildStaticParityReport, type ParityReport } from '@/lib/parityCheck';
import type { LayoutBlock } from '@web-studio/shared-schemas';
import { useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ParityPanelProps {
  blocks: LayoutBlock[];
  locale: string;
  themeId: string;
  colorsetId: string;
  onClose?: () => void;
}

/** SSR Checkup popup — two side-by-side markup panes over the lab canvas. */
export function ParityPanel({
  blocks,
  locale,
  themeId,
  colorsetId,
  onClose,
}: ParityPanelProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  const report = useMemo(() => {
    try {
      return buildStaticParityReport(blocks, locale, themeId, colorsetId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        reactLength: 0,
        ssrLength: 0,
        matches: false,
        structuralMatch: false,
        reactExcerpt: `Failed to build React markup:\n${message}`,
        ssrExcerpt: `Failed to build SSR markup:\n${message}`,
      } satisfies ParityReport;
    }
  }, [blocks, locale, themeId, colorsetId]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="lab-parity-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="lab-parity-window"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="lab-parity-window__head">
          <div>
            <h2 id={titleId} className="lab-parity-window__title">
              SSR Checkup
            </h2>
            <p className="lab-parity-window__desc">
              Static React <code>SitePage</code> (editable:false) vs <code>renderSiteHtml</code>{' '}
              &lt;main&gt;
            </p>
          </div>
          <div className="lab-parity-window__head-actions">
            <ParitySummary report={report} />
            {onClose ? (
              <button
                ref={closeRef}
                type="button"
                className="lab-btn lab-btn--ghost"
                onClick={onClose}
              >
                Close
              </button>
            ) : null}
          </div>
        </header>

        <div className="lab-parity-window__panes">
          <article className="lab-parity-window__pane">
            <header className="lab-parity-window__pane-head">
              <h3 className="lab-parity-window__pane-title">React SitePage</h3>
              <span className="lab-parity-window__pane-meta">{report.reactLength} chars</span>
            </header>
            <pre className="lab-parity-window__pre">{report.reactExcerpt}</pre>
          </article>

          <article className="lab-parity-window__pane">
            <header className="lab-parity-window__pane-head">
              <h3 className="lab-parity-window__pane-title">Rendered SSR</h3>
              <span className="lab-parity-window__pane-meta">{report.ssrLength} chars</span>
            </header>
            <pre className="lab-parity-window__pre">{report.ssrExcerpt}</pre>
          </article>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ParitySummary({ report }: { report: ParityReport }) {
  return (
    <div className="lab-parity-window__badges">
      <span
        className={`lab-badge ${report.structuralMatch ? 'lab-badge--ok' : 'lab-badge--warn'}`}
      >
        structural {report.structuralMatch ? 'match' : 'mismatch'}
      </span>
      <span className={`lab-badge ${report.matches ? 'lab-badge--ok' : 'lab-badge--warn'}`}>
        HTML {report.matches ? 'match' : 'mismatch'}
      </span>
    </div>
  );
}
